import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { i18n } from '@/i18n';
import { useErrorDialogStore } from '@/stores/errorDialog';
import {
  buildErrorDialogPayload,
  isGlobalErrorSkipped,
  type GlobalErrorConfig,
} from '@/api/globalErrorInterceptor';
import { attachActivityInterceptors } from '@/api/requestActivity';
import { baseUrlForPath, v2ServiceForPath, type ApiHostEnv } from '@/api/apiHosts';

/**
 * Authenticated HTTP client. Every outbound request runs the request
 * interceptor which calls Amplify's `fetchAuthSession()` to pull the
 * current Cognito ID token and attaches it as `Authorization: Bearer …`.
 *
 * There is still exactly ONE authenticated client. Requests are split
 * between the legacy host and the V2 platform by path (`/employment/…`,
 * `/identity/…` → V2 when switched on), not by having a second instance —
 * so the auth, activity and error interceptors below apply to every call
 * whichever host answers it. See `apiHosts.ts`.
 *
 * The 401 handler clears the session (signs the user out) and pushes them
 * to /sign-in via the global router instance.
 *
 * A second response interceptor surfaces a global error dialog for any
 * failed backend response (4xx/5xx) or network failure — see
 * `globalErrorInterceptor.ts`. It is additive: the error is always
 * re-rejected so existing per-call handlers still run. A call can opt out
 * with `{ skipGlobalError: true }` in its request config.
 *
 * Tokens are NEVER written to localStorage by this layer — Amplify handles
 * its own storage adapter (D7 in IMPLEMENTATION_PLAN.md is locked).
 */

// Augment axios so any request config accepts the opt-out flag with full
// type-safety: `httpClient.get(url, { skipGlobalError: true })`.
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AxiosRequestConfig extends GlobalErrorConfig {}
}

export const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30_000,
});

// Global in-flight-request activity instrumentation (drives the top progress
// bar). Attached first so its response handler decrements the counter before
// the error-dialog handler runs; both re-throw, so ordering is non-fatal.
// Counted exactly once per logical request — see requestActivity.ts.
attachActivityInterceptors(httpClient);

/**
 * Send a V2-shaped path to the V2 host, leaving every other request exactly
 * as it was. Applied per request (not baked into the instance) so the env
 * kill switch decides at call time and the instance keeps one set of
 * interceptors.
 * @param config The outgoing axios request config.
 * @param env Optional host env override, for tests.
 * @returns The same config, with `baseURL` re-pointed when the path is a
 * switched-on V2 path.
 */
export function routeToServiceHost<T extends InternalAxiosRequestConfig>(
  config: T,
  env?: ApiHostEnv,
): T {
  if (!v2ServiceForPath(config.url)) return config;
  const base = env ? baseUrlForPath(config.url, env) : baseUrlForPath(config.url);
  config.baseURL = base;
  return config;
}

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) =>
  routeToServiceHost(config),
);

httpClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      config.headers.set('Authorization', `Bearer ${idToken}`);
    }
  } catch {
    // No session — let the request go through; the API will return 401 and
    // the response interceptor will redirect to sign-in.
  }
  return config;
});

/**
 * Global failed-response interceptor. Runs on every error response (4xx/5xx)
 * and network failure. It shows the calm error dialog and then re-rejects so
 * downstream `.catch` / TanStack `onError` still fire.
 *
 * 401 is intentionally excluded: the auth handler below signs the user out
 * and redirects to sign-in, so a "you don't have access" dialog on top of
 * that navigation would be redundant noise.
 *
 * Installed eagerly at module load so it is active before the auth handler
 * (which is wired in `main.ts`). Interceptors run in FIFO registration order,
 * so this dialog interceptor sees the error first; both re-throw.
 */
httpClient.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    const status = err.response?.status;
    const skipped = isGlobalErrorSkipped(err.config);
    if (!skipped && status !== 401) {
      try {
        const store = useErrorDialogStore();
        store.show(buildErrorDialogPayload(err, (key) => i18n.global.t(key)));
      } catch {
        // Pinia not active yet (e.g. a request fired during boot before the
        // store is installed) — never let dialog plumbing swallow the error.
      }
    }
    throw err;
  },
);

/**
 * Hook the auth response interceptor lazily so we can inject the router and
 * sign-out callback without creating a circular import with the auth store.
 */
export function installResponseAuthHandler(handler: {
  onUnauthorized: () => Promise<void> | void;
}) {
  httpClient.interceptors.response.use(
    (r) => r,
    async (err: AxiosError) => {
      if (err.response?.status === 401) {
        await handler.onUnauthorized();
      }
      throw err;
    },
  );
}
